import { useCallback, useSyncExternalStore } from 'react'

export const DOC_CHAT_MULTI_CALL_STORAGE_KEY =
  'documentSearch_docChatMultiCall:v1'

const DEFAULT_ENABLED = true

const listeners = new Set<() => void>()

let snapshot: boolean = loadDocChatMultiCallEnabled()

function emit(): void {
  listeners.forEach((listener) => listener())
}

function persist(enabled: boolean): void {
  try {
    localStorage.setItem(
      DOC_CHAT_MULTI_CALL_STORAGE_KEY,
      JSON.stringify(enabled)
    )
  } catch {
    /* localStorage unavailable */
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): boolean {
  return snapshot
}

function getServerSnapshot(): boolean {
  return DEFAULT_ENABLED
}

export function loadDocChatMultiCallEnabled(): boolean {
  try {
    const raw = localStorage.getItem(DOC_CHAT_MULTI_CALL_STORAGE_KEY)
    if (raw === null) return DEFAULT_ENABLED
    const parsed: unknown = JSON.parse(raw)
    return typeof parsed === 'boolean' ? parsed : DEFAULT_ENABLED
  } catch {
    return DEFAULT_ENABLED
  }
}

export function setDocChatMultiCallEnabled(enabled: boolean): void {
  snapshot = enabled
  persist(enabled)
  emit()
}

/**
 * Whether the user wants the Multicall agent on their chat turns. This is
 * the user's preference only: the backend capability manifest still decides
 * whether the agent is available, and a turn runs it only when both agree.
 */
export function useDocChatMultiCallSetting(): {
  enabled: boolean
  setEnabled: (enabled: boolean) => void
} {
  const enabled = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot
  )
  const setEnabled = useCallback((next: boolean) => {
    setDocChatMultiCallEnabled(next)
  }, [])
  return { enabled, setEnabled }
}
